import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Format der Anmelde-Referenznummer: `REG-XXXXXX` — fester Präfix `REG-`
 * gefolgt von 6 Zeichen aus `23456789ABCDEFGHJKLMNPQRSTUVWXYZ`
 * (Großbuchstaben, ohne die leicht verwechselbaren 0/1/I/O). Siehe
 * `generateReferenceNumber` in `createStudentRegistration.ts`.
 *
 * Im Kontaktformular wird `REG-` als fester Präfix angezeigt; der Form-Control
 * enthält nur den 6-stelligen Body.
 */
export const REFERENCE_NUMBER_PREFIX = 'REG-';

/** Genau 6 Zeichen aus dem reduzierten Alphabet (ohne 0/1/I/O). */
export const REFERENCE_NUMBER_BODY_PATTERN = /^[2-9A-HJ-NP-Z]{6}$/;

/**
 * Normalisiert eine Roh-Eingabe auf den Body (ohne Präfix):
 * - Whitespaces entfernen
 * - Großbuchstaben
 * - optionales führendes `REG` samt aller direkt folgenden Bindestriche entfernen
 *
 * Es werden bewusst KEINE sonstigen Zeichen herausgefiltert: Tippt jemand eine
 * Nummer falsch ab, soll die Eingabe erkennbar bleiben und die Validierung
 * greift.
 */
export function normalizeReferenceNumberBody(
  value: string | null | undefined
): string {
  if (!value) {
    return '';
  }
  const cleaned = value.replace(/\s+/g, '').toUpperCase();
  return cleaned.replace(/^(REG)?-*/, '');
}

/** Prüft, ob ein Body dem geforderten Format entspricht. */
export function isValidReferenceNumberBody(body: string): boolean {
  return REFERENCE_NUMBER_BODY_PATTERN.test(body);
}

/**
 * Baut den vollständigen Wert für den Versand. Leerer Body → leerer String
 * (Feld ist optional).
 */
export function buildReferenceNumber(
  body: string | null | undefined
): string {
  const normalized = normalizeReferenceNumberBody(body);
  return normalized ? REFERENCE_NUMBER_PREFIX + normalized : '';
}

/**
 * Validator: leer ist erlaubt (optionales Feld); ist etwas eingegeben, muss
 * der Body dem Format entsprechen.
 */
export function referenceNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const body = control.value as string | null | undefined;
    if (!body) {
      return null;
    }
    return isValidReferenceNumberBody(body) ? null : { referenceNumber: true };
  };
}
